import { type KeyboardEvent, type ReactNode } from 'react';

import { ArrowIcon } from '@/lib/shared/icons/ArrowIcon';

import { clsx } from 'clsx';
import Link from 'next/link';

import styles from './BlogPagination.module.scss';
import { getVisiblePages } from './visible-pages';

type BlogPaginationProps = {
  currentPage: number;
  maxPages: number;
  hrefForPage?: (page: number) => string;
  onPageChange?: (page: number) => void;
  className?: string;
};

export const BlogPagination = ({
  currentPage,
  maxPages,
  hrefForPage,
  onPageChange,
  className,
}: BlogPaginationProps) => {
  if (maxPages <= 1) {
    return null;
  }

  const pages = getVisiblePages(currentPage, maxPages);

  const handleKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    page: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onPageChange?.(page);
    }
  };

  const renderControl = (
    page: number,
    content: ReactNode,
    controlClassName: string,
    ariaLabel: string,
    disabled: boolean,
    isCurrent = false
  ) => {
    if (disabled) {
      return (
        <button
          type="button"
          className={clsx(
            controlClassName,
            styles['pagination__control--disabled']
          )}
          aria-label={ariaLabel}
          disabled
        >
          {content}
        </button>
      );
    }

    if (hrefForPage) {
      return (
        <Link
          href={hrefForPage(page)}
          className={controlClassName}
          aria-label={ariaLabel}
          aria-current={isCurrent ? 'page' : undefined}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        type="button"
        className={controlClassName}
        aria-label={ariaLabel}
        aria-current={isCurrent ? 'page' : undefined}
        onClick={() => onPageChange?.(page)}
        onKeyDown={(event) => handleKeyDown(event, page)}
      >
        {content}
      </button>
    );
  };

  return (
    <nav className={clsx(styles.pagination, className)} aria-label="Pagination">
      {renderControl(
        currentPage - 1,
        <ArrowIcon className={styles.pagination__arrowIcon} />,
        styles.pagination__arrow,
        'Previous page',
        currentPage === 1
      )}

      {pages.map((page, index) => {
        if (page === 'dots') {
          return (
            <span
              key={`dots-${index}`}
              className={styles.pagination__dots}
              aria-hidden
            >
              ...
            </span>
          );
        }

        const isActive = page === currentPage;

        return (
          <span key={page} className={styles.pagination__item}>
            {renderControl(
              page,
              page,
              clsx(
                styles.pagination__button,
                isActive && styles['pagination__button--active']
              ),
              `Page ${page}`,
              false,
              isActive
            )}
          </span>
        );
      })}

      {renderControl(
        currentPage + 1,
        <ArrowIcon className={styles.pagination__arrowIcon} isRight />,
        styles.pagination__arrow,
        'Next page',
        currentPage === maxPages
      )}
    </nav>
  );
};
